import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink
} from 'firebase/auth';
import { auth } from '../firebase';
import { doc, setDoc, getDoc, serverTimestamp, collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

// Constants for session timeout
const SESSION_TIMEOUT_MS = 8 * 60 * 60 * 1000; // 8 hours in milliseconds
const LOGIN_TIMESTAMP_KEY = 'slavex_login_timestamp';

// Helper to get backend API base URL (same logic used in pages)
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;

  if (!envUrl) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('your-cloud-run-url') || envUrl.includes('placeholder')) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('run.app')) {
    return envUrl;
  }
  if (process.env.NODE_ENV === 'production') {
    return envUrl;
  }
  return 'http://localhost:8081';
};

const API_BASE_URL = getApiBaseUrl();

// Helper function to update version on login
function updateVersionOnLogin() {
  const currentBuildNumber = process.env.REACT_APP_BUILD_NUMBER || '0';
  const storedVersion = localStorage.getItem('app_version');
  if (!storedVersion || storedVersion !== currentBuildNumber) {
    // Update to current version on login
    localStorage.setItem('app_version', currentBuildNumber);
  }
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]); // Keep as array for backward compatibility, but will only have 1 role
  const [currentRole, setCurrentRole] = useState(null); // Currently active role (single role)
  const [loading, setLoading] = useState(true);
  const [switchingRole, setSwitchingRole] = useState(false); // Track if user is switching roles
  const [isApproved, setIsApproved] = useState(false); // Whether user has passed email verification / approval
  // Check if session has expired (8 hours)
  function isSessionExpired() {
    const loginTimestamp = localStorage.getItem(LOGIN_TIMESTAMP_KEY);
    if (!loginTimestamp) {
      return false; // No timestamp means fresh login, not expired
    }

    const loginTime = parseInt(loginTimestamp, 10);
    if (isNaN(loginTime)) {
      return false; // Invalid timestamp, treat as fresh login
    }

    const now = Date.now();
    const elapsed = now - loginTime;

    return elapsed > SESSION_TIMEOUT_MS;
  }

  // Save login timestamp to localStorage
  function saveLoginTimestamp() {
    localStorage.setItem(LOGIN_TIMESTAMP_KEY, Date.now().toString());
  }

  // Clear login timestamp from localStorage
  function clearLoginTimestamp() {
    localStorage.removeItem(LOGIN_TIMESTAMP_KEY);
  }

  // Ensure user profile fields (email, displayName, photoURL) are saved/updated in Firestore user doc
  async function ensureProfileFieldsSaved(uid, { email, displayName, photoURL }) {
    try {
      if (!uid) return;
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (!userDoc.exists()) {
        // Do not create here to keep flow consistent with role creation
        return;
      }
      const userData = userDoc.data() || {};
      const updates = {};
      let needsUpdate = false;

      if (email && userData.email !== email) {
        updates.email = email;
        needsUpdate = true;
      }
      if (typeof displayName !== 'undefined') {
        // Only update if displayName is not null/undefined/empty AND different from existing
        // Critical: Never overwrite existing Firestore displayName with null/empty from Firebase Auth
        // If Firebase Auth doesn't have displayName but Firestore does, preserve Firestore value
        const existingDisplayName = userData.displayName;
        const newDisplayName = displayName && displayName.trim() ? displayName.trim() : null;

        // Only update if:
        // 1. We have a valid new displayName AND it's different from existing, OR
        // 2. We're explicitly setting it (not just syncing from Auth)
        // But NEVER overwrite existing with null/empty
        if (newDisplayName && newDisplayName !== existingDisplayName) {
          updates.displayName = newDisplayName;
          needsUpdate = true;
        }
        // If newDisplayName is null/empty but existingDisplayName exists, do NOT update
        // This preserves Firestore displayName even if Firebase Auth doesn't have it
      }
      if (typeof photoURL !== 'undefined' && userData.photoURL !== photoURL) {
        updates.photoURL = photoURL || null;
        needsUpdate = true;
      }

      if (needsUpdate) {
        updates.updatedAt = serverTimestamp();
        await setDoc(doc(db, 'users', uid), updates, { merge: true });
      }
    } catch (error) {
      console.error('Error ensuring profile fields saved:', error);
      // Non-critical, do not throw
    }
  }

  // Handle session expiration by logging out
  const handleSessionExpiration = useCallback(async () => {
    console.log('Session expired (8 hours). Logging out...');
    clearLoginTimestamp();
    await signOut(auth);
    // Clear user state
    setCurrentUser(null);
    setUserRoles([]);
    setCurrentRole(null);
  }, []);

  // Update the most recent login log with displayName and photoURL from Firestore
  async function updateRecentLoginLog(uid, displayName, photoURL) {
    try {
      if (!displayName && !photoURL) {
        return; // Nothing to update
      }

      // Query for the most recent login log for this user
      const loginLogsRef = collection(db, 'loginLogs');
      const q = query(
        loginLogsRef,
        where('userId', '==', uid),
        orderBy('loginTimestamp', 'desc'),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const logDoc = querySnapshot.docs[0];
        const logData = logDoc.data();
        const updates = {};

        // Only update if the log currently has null/undefined displayName and we have a new one
        if ((!logData.displayName || logData.displayName === null) && displayName) {
          updates.displayName = displayName;
        }

        // Only update if the log currently has null/undefined photoURL and we have a new one
        if ((!logData.photoURL || logData.photoURL === null) && photoURL) {
          updates.photoURL = photoURL;
        }

        // Update the log if we have any changes
        if (Object.keys(updates).length > 0) {
          await setDoc(logDoc.ref, updates, { merge: true });
        }
      }
    } catch (error) {
      // Non-critical error, don't throw
      console.warn('Failed to update recent login log:', error);
    }
  }

  // Log login event to Firestore
  async function logLoginEvent(user, loginMethod = 'email') {
    try {
      // Skip logging if user email is missing - this prevents false login logs during signup
      // when the user might not have email set yet
      if (!user || !user.email || !user.uid) {
        console.warn('Skipping login log: user email or uid is missing', { email: user?.email, uid: user?.uid });
        return;
      }

      // Create document ID from readable timestamp and email
      const now = new Date();
      // Format: YYYY-MM-DD_HH-MM-SS-mmm (year-month-day_hour-minute-second-milliseconds)
      const readableTimestamp = now.toISOString()
        .replace(/T/, '_')
        .replace(/:/g, '-')
        .replace(/\.(\d{3})Z$/, '-$1') // Keep milliseconds, remove Z
        .replace(/Z$/, ''); // Fallback if no milliseconds
      // Sanitize email for Firestore document ID (replace invalid chars with underscore)
      const sanitizedEmail = user.email.replace(/[^a-zA-Z0-9._-]/g, '_');
      const docId = `${readableTimestamp}_${sanitizedEmail}`;

      // Try to get displayName from Firestore user document if not available in Auth user
      let displayName = user.displayName || null;
      let photoURL = user.photoURL || null;

      if (!displayName || !photoURL) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Use Firestore displayName if Auth user doesn't have it
            if (!displayName && userData.displayName) {
              displayName = userData.displayName;
            }
            // Use Firestore photoURL if Auth user doesn't have it
            if (!photoURL && userData.photoURL) {
              photoURL = userData.photoURL;
            }
          }
        } catch (error) {
          // Non-critical error, continue with Auth user data
          console.warn('Failed to fetch user document for login log:', error);
        }
      }

      const loginLogData = {
        userId: user.uid,
        userEmail: user.email,
        displayName: displayName,
        loginMethod: loginMethod, // 'email' or 'google'
        loginTimestamp: serverTimestamp(),
        photoURL: photoURL
      };

      if (user.email === "eyaldana04@gmail.com") {
        return;
      };

      await setDoc(doc(db, 'loginLogs', docId), loginLogData);
    } catch (error) {
      console.error('Error logging login event:', error);
      // Don't throw - logging is not critical for login functionality
    }
  }

  async function signup(email, password, role) {
    // Use custom signup endpoint
    const response = await fetch(`${API_BASE_URL}/api/auth/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password })
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.error || 'Failed to sign up');
      if (data.code === 'auth/google-account-exists' || (data.error && data.error.includes('already registered with Google'))) {
        error.code = 'auth/google-account-exists';
      } else if (data.error && data.error.includes('already in use')) {
        error.code = 'auth/email-already-in-use';
      }
      throw error;
    }

    // Sign in with custom token
    const customToken = data.customToken;
    const result = await signInWithCustomToken(auth, customToken);

    if (result.user) {
      // Only save role if provided (user will select role after signup if null)
      if (role) {
        await saveUserRole(result.user.uid, role);
      } else {
        // Ensure user document exists with email
        await ensureEmailSaved(result.user.uid, email);
      }

      // Sync profile fields to user doc on signup
      await ensureProfileFieldsSaved(result.user.uid, {
        email: result.user.email,
        displayName: result.user.displayName || null,
        photoURL: result.user.photoURL || null
      });

      // Don't log login event during signup - wait until user actually logs in after verification
      // This prevents false login logs with missing email/user data
      // Login events will be logged when user signs in via the login() function after email verification

      // Save login timestamp
      saveLoginTimestamp();
      // Update version on login
      updateVersionOnLogin();
    }

    // Return result with requiresVerification flag
    return {
      ...result,
      requiresVerification: data.requiresVerification
    };
  }

  async function resendVerificationCode(email) {
    // Use resend verification code endpoint
    const response = await fetch(`${API_BASE_URL}/api/auth/resend-verification-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to resend verification code');
    }

    return data;
  }

  async function sendEmailLink(email, actionCodeSettings) {
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    // Save email locally for when user completes sign-in on same device
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('emailForSignIn', email);
    }
  }

  function checkIsSignInWithEmailLink(url) {
    return isSignInWithEmailLink(auth, url);
  }

  async function completeSignInWithEmailLink(email, emailLink) {
    const result = await signInWithEmailLink(auth, email, emailLink);
    if (result.user) {
      // Ensure email is saved (for new users, this creates the document)
      await ensureEmailSaved(result.user.uid, result.user.email);
      await loadUserRole(result.user.uid);
      // Log login event
      await logLoginEvent(result.user, 'emailLink');
      // Save login timestamp
      saveLoginTimestamp();
      // Update version on login
      updateVersionOnLogin();
      // Sync profile fields to user doc on login
      await ensureProfileFieldsSaved(result.user.uid, {
        email: result.user.email,
        displayName: result.user.displayName || null,
        photoURL: result.user.photoURL || null
      });
      // Clear email from storage
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem('emailForSignIn');
      }
    }
    return result;
  }

  /**
   * Login with email & password using custom authentication.
   *
   * If the user is not yet approved, this function will:
   *   1) Trigger sending a 6-digit verification code via backend SMTP
   *   2) Throw an Error with code 'auth/code-required' so the UI can prompt for the code
   *
   * When called again with the same credentials and a verificationCode,
   * it will verify the code via the backend and complete the login flow.
   */
  async function login(email, password, verificationCode = null) {
    // Use custom signin endpoint
    const response = await fetch(`${API_BASE_URL}/api/auth/signin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        password,
        verificationCode: verificationCode || undefined
      })
    });

    const data = await response.json();

    if (!response.ok) {
      // Check if this is a Google account without password
      if (data.code === 'auth/google-account-no-password' || (data.error && data.error.includes('registered with Google'))) {
        const err = new Error(data.error || 'This email is registered with Google');
        err.code = 'auth/google-account-no-password';
        throw err;
      }

      // Check if verification is required
      if (data.requiresVerification || response.status === 403) {
        const err = new Error(data.error || 'Email verification required');
        err.code = 'auth/code-required';
        throw err;
      }

      // Check for invalid verification code
      if (data.error && (data.error.includes('Invalid verification code') || data.error.includes('verification code'))) {
        const err = new Error(data.error);
        err.code = 'auth/invalid-verification-code';
        throw err;
      }

      // Other errors
      const err = new Error(data.error || 'Failed to sign in');
      if (data.error && data.error.includes('Invalid email or password')) {
        err.code = 'auth/wrong-password';
      } else if (data.error && data.error.includes('not found')) {
        err.code = 'auth/user-not-found';
      }
      throw err;
    }

    // Sign in with custom token
    const customToken = data.customToken;
    const result = await signInWithCustomToken(auth, customToken);

    if (result.user) {
      await loadUserRole(result.user.uid);
      // Log login event
      await logLoginEvent(result.user, 'email');
      // Save login timestamp
      saveLoginTimestamp();
      // Update version on login
      updateVersionOnLogin();
      // Sync profile fields to user doc on login
      await ensureProfileFieldsSaved(result.user.uid, {
        email: result.user.email,
        displayName: result.user.displayName || null,
        photoURL: result.user.photoURL || null
      });
    }

    return {
      ...result,
      approved: data.approved,
      requiresVerification: data.requiresVerification
    };
  }

  async function loginWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      // Add additional scopes if needed
      provider.addScope('profile');
      provider.addScope('email');

      // Set custom parameters
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      const result = await signInWithPopup(auth, provider);
      if (result.user) {
        // Check if user already has a role, if not, redirect to role selection
        await loadUserRole(result.user.uid);
        // Log login event
        await logLoginEvent(result.user, 'google');
        // Save login timestamp
        saveLoginTimestamp();
        // Update version on login
        updateVersionOnLogin();
        // Sync profile fields to user doc on login
        await ensureProfileFieldsSaved(result.user.uid, {
          email: result.user.email,
          displayName: result.user.displayName || null,
          photoURL: result.user.photoURL || null
        });
      }
      return result;
    } catch (error) {
      console.error('Google login error details:', {
        code: error.code,
        message: error.message,
        email: error.email,
        credential: error.credential,
        fullError: error
      });

      // Provide more helpful error messages
      if (error.code === 'auth/popup-blocked') {
        throw new Error('Popup was blocked by your browser. Please allow popups for this site and try again.');
      } else if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in popup was closed. Please try again.');
      } else if (error.code === 'auth/cancelled-popup-request') {
        throw new Error('Only one popup request is allowed at a time. Please try again.');
      } else if (error.code === 'auth/unauthorized-domain') {
        throw new Error('This domain is not authorized for Firebase Authentication. Please contact support.');
      } else if (error.code === 'auth/operation-not-allowed') {
        throw new Error('Google sign-in is not enabled. Please contact support.');
      } else if (error.code === 'auth/internal-error') {
        throw new Error('An internal error occurred. This may be due to Firebase configuration. Please check: 1) Google OAuth is enabled in Firebase Console, 2) Authorized domains include this site, 3) OAuth consent screen is configured in Google Cloud Console.');
      }
      throw error;
    }
  }

  async function addUserRole(uid, newRole, businessType = null, displayName = null) {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));

      // Get user email from currentUser or from existing document
      let userEmail = currentUser?.email;
      if (!userEmail && userDoc.exists()) {
        userEmail = userDoc.data().email;
      }

      // Build roles: merge existing roles with newRole instead of replacing
      let mergedRoles = [newRole];
      if (userDoc.exists()) {
        const existingData = userDoc.data();
        const existingRoles = Array.isArray(existingData.roles) ? existingData.roles : [];
        const existingRole = existingData.role;
        mergedRoles = Array.from(
          new Set([
            ...existingRoles.map(String),
            ...(existingRole ? [String(existingRole)] : []),
            String(newRole)
          ])
        );
      }

      const userDataToSave = {
        // active_role is the currently selected role
        active_role: newRole,
        // keep single role for backwards compatibility (primary = newRole)
        role: newRole,
        // full list of roles this user has profiles for
        roles: mergedRoles,
        updatedAt: serverTimestamp()
      };

      // Add email if we have it
      if (userEmail) {
        userDataToSave.email = userEmail;
      }

      // Do NOT clear business-specific fields when adding freelancer role
      // A user can legitimately be both business and freelancer.

      // Add business category if it's a business role
      if (newRole === 'business' && businessType) {
        // businessType parameter can now contain businessCategory value
        userDataToSave.businessCategory = businessType;
        // Keep businessType for backward compatibility
        userDataToSave.businessType = businessType;
      }

      // Add display name for all roles (required for both business and freelancer)
      if (displayName) {
        userDataToSave.displayName = displayName;
      }

      // Add createdAt only if it doesn't exist (new user)
      if (!userDoc.exists()) {
        userDataToSave.createdAt = serverTimestamp();
        // Require KYC (Sumsub) only for brand-new signups (legacy users won't have this flag)
        userDataToSave.kycRequired = true;
        userDataToSave.kycCompleted = false;
      }

      await setDoc(doc(db, 'users', uid), userDataToSave, { merge: true });

      // Update the most recent login log with displayName if it was null
      // This handles the case where login log was created during signup before displayName was set
      if (displayName) {
        // Get photoURL from user document if available
        const finalUserDoc = await getDoc(doc(db, 'users', uid));
        const finalUserData = finalUserDoc.exists() ? finalUserDoc.data() : {};
        await updateRecentLoginLog(uid, displayName, finalUserData.photoURL || null);
      }

      setUserRoles(mergedRoles);
      setCurrentRole(newRole);
      setSwitchingRole(false);
      // Initialize persistent freelancer level entry (survives account deletion)
      try {
        if (newRole === 'freelancer' && (currentUser?.email || userDoc.data()?.email)) {
          const emailToUse = (currentUser?.email || userDoc.data()?.email || '').toLowerCase();
          if (emailToUse) {
            const token = await (currentUser ? currentUser.getIdToken(true) : auth.currentUser.getIdToken(true));
            const API_BASE_URL = process.env.REACT_APP_API_URL && (process.env.REACT_APP_API_URL.includes('run.app') || process.env.NODE_ENV === 'production')
              ? process.env.REACT_APP_API_URL
              : (process.env.NODE_ENV === 'production' ? (process.env.REACT_APP_API_URL || '') : 'http://localhost:8081');
            await fetch(`${API_BASE_URL}/api/freelancer/level`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ level: 1 })
            }).catch(() => {});
          }
        }
      } catch (e) {
        console.warn('Failed to initialize freelancer level:', e);
      }
    } catch (error) {
      console.error('Error setting user role:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      setSwitchingRole(false);
      throw error;
    }
  }

  async function switchRole(newRole) {
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    setSwitchingRole(true);
    try {
      // Get API base URL
      const API_BASE_URL = process.env.REACT_APP_API_URL && (process.env.REACT_APP_API_URL.includes('run.app') || process.env.NODE_ENV === 'production')
        ? process.env.REACT_APP_API_URL
        : (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081');

      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/auth/switch-role`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ newRole })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to switch role');
      }

      const data = await response.json();

      // Update local state with the new active role
      if (data.activeRole) {
        setCurrentRole(data.activeRole);
        // Update userRoles to include all available roles
        if (data.availableRoles && Array.isArray(data.availableRoles)) {
          setUserRoles(data.availableRoles);
        }
      }

      setSwitchingRole(false);
      return data;
    } catch (error) {
      console.error('Error switching role:', error);
      setSwitchingRole(false);
      throw error;
    }
  }

  async function saveUserRole(uid, role) {
    // This is for backward compatibility - adds the first role
    await addUserRole(uid, role);
  }

  async function loadUserRoles(uid) {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();

        // Derive approval status from Firestore document, mirroring backend logic:
        //  - If "approved" flag exists, use it directly.
        //  - Otherwise, treat emailVerified/email_verified as approval (backwards compatibility).
        let approved = false;
        if (Object.prototype.hasOwnProperty.call(userData, 'approved')) {
          approved = !!userData.approved;
        } else if (userData.emailVerified || userData.email_verified) {
          approved = true;
        }
        setIsApproved(approved);

        // Check for active_role first (new field for role switching)
        let role = userData.active_role;

        // Fallback to old role fields if active_role not set
        if (!role) {
          if (userData.role) {
            role = userData.role;
          } else if (userData.roles && Array.isArray(userData.roles) && userData.roles.length > 0) {
            // Migrate from array to single role
            role = userData.roles[0];
            // Update Firestore to single role format
            await setDoc(doc(db, 'users', uid), {
              role: role,
              roles: [role],
              active_role: role
            }, { merge: true });
          }
        }

        // Get all available roles
        let allRoles = [];
        if (userData.roles && Array.isArray(userData.roles)) {
          allRoles = userData.roles;
        } else if (userData.role) {
          allRoles = [userData.role];
        }

        if (role) {
          setUserRoles(allRoles.length > 0 ? allRoles : [role]);
          setCurrentRole(role);
        } else {
          setUserRoles([]);
          setCurrentRole(null);
        }
      } else {
        // User doesn't have any roles yet
        setUserRoles([]);
        setCurrentRole(null);
        setIsApproved(false);
      }
    } catch (error) {
      console.error('Error loading user roles:', error);
      setUserRoles([]);
      setCurrentRole(null);
      setIsApproved(false);
    }
  }

  // Backward compatibility alias
  async function loadUserRole(uid) {
    await loadUserRoles(uid);
  }

  async function ensureEmailSaved(uid, email) {
    try {
      if (!email) return;

      const userDoc = await getDoc(doc(db, 'users', uid));
      if (!userDoc.exists()) {
        // User doesn't exist yet, will be created when role is added
        return;
      }

      const userData = userDoc.data();
      // Only update if email is missing or different
      if (!userData.email || userData.email !== email) {
        await setDoc(doc(db, 'users', uid), {
          email: email,
          updatedAt: serverTimestamp()
        }, { merge: true });
      }
    } catch (error) {
      console.error('Error saving email:', error);
      // Don't throw - this is not critical
    }
  }

  async function addPassword(password) {
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    // Check if user signed in with Google
    const providerIds = (currentUser.providerData || []).map((p) => p.providerId);
    const isGoogle = providerIds.includes('google.com');

    if (!isGoogle) {
      throw new Error('This feature is only available for Google-authenticated users');
    }

    const token = await currentUser.getIdToken();
    const response = await fetch(`${API_BASE_URL}/api/auth/add-password`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ password })
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.error || 'Failed to add password');
      throw error;
    }

    return data;
  }

  function logout() {
    clearLoginTimestamp();
    return signOut(auth);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Check if session has expired (only if timestamp exists)
        // If no timestamp exists, it's a fresh login, so don't expire it
        const loginTimestamp = localStorage.getItem(LOGIN_TIMESTAMP_KEY);
        if (loginTimestamp && isSessionExpired()) {
          await handleSessionExpiration();
          setLoading(false);
          return;
        }

        setCurrentUser(user);
        await loadUserRoles(user.uid);

        // If the user is an admin or signed in with Google, treat them as approved
        // regardless of Firestore flags (matches backend _is_user_approved rules).
        try {
          const providerIds = (user.providerData || []).map((p) => p.providerId);
          const isGoogle = providerIds.includes('google.com');
          if (isGoogle) {
            setIsApproved(true);
          }
        } catch (e) {
          console.warn('Failed to inspect auth providers for approval logic:', e);
        }
        // Ensure profile fields are saved/updated in the user document
        await ensureProfileFieldsSaved(user.uid, {
          email: user.email,
          displayName: user.displayName || null,
          photoURL: user.photoURL || null
        });

        // If there's no login timestamp, this is a fresh login or restored session
        // Set it now to start the 8-hour timer
        if (!loginTimestamp) {
          saveLoginTimestamp();
        }
      } else {
        setCurrentUser(null);
        setUserRoles([]);
        setCurrentRole(null);
        setIsApproved(false);
        clearLoginTimestamp();
      }
      setLoading(false);
    });

    // Listen for storage changes (for cross-tab synchronization)
    const handleStorageChange = (e) => {
      if (e.key === LOGIN_TIMESTAMP_KEY || e.key === null) {
        // Login timestamp was cleared or storage was cleared
        if (currentUser && isSessionExpired()) {
          handleSessionExpiration();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Periodic check for session expiration (every minute)
    const sessionCheckInterval = setInterval(() => {
      if (currentUser && isSessionExpired()) {
        handleSessionExpiration();
      }
    }, 60000); // Check every minute

    return () => {
      unsubscribe();
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(sessionCheckInterval);
    };
  }, [currentUser, handleSessionExpiration]);

  const value = {
    currentUser,
    userRoles, // Array for backward compatibility, but will only have 1 role
    currentRole, // Currently active role (single role)
    userRole: currentRole, // Backward compatibility
    isApproved, // Whether user has passed email verification / approval
    switchingRole, // Whether user is switching roles
    loading,
    login,
    signup,
    logout,
    loginWithGoogle,
    saveUserRole,
    addUserRole,
    switchRole,
    loadUserRoles,
    resendVerificationCode,
    sendEmailLink,
    checkIsSignInWithEmailLink,
    completeSignInWithEmailLink,
    addPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

